import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';
import Attachments from '../components/Attachments';

const PAYMENT_MODE_OPTIONS = ['Cash', 'UPI', 'Bank'];
const STATUSES = ['pending', 'approved', 'rejected'];

const emptyForm = () => ({
  date: '',
  expenseNature: '',
  contractorName: '',
  projectName: '',
  billNumber: '',
  amount: '',
  paymentMode: 'Cash',
  paidBy: '',
  paidAmount: '',
  remarks: '',
});

const AdminExpenseSheet = () => {
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [natures, setNatures] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [fEmployee, setFEmployee] = useState('');
  const [fDate, setFDate] = useState('');
  const [fNature, setFNature] = useState('');
  const [fContractor, setFContractor] = useState('');
  const [fProject, setFProject] = useState('');
  const [fStatus, setFStatus] = useState('');

  const [viewExpense, setViewExpense] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [review, setReview] = useState(null); // { id, status }
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewRemark, setReviewRemark] = useState('');
  const [payExpense, setPayExpense] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', date: '', remarks: '' });
  const [deleteExpense, setDeleteExpense] = useState(null);

  const toast = useToast();

  const loadMeta = async () => {
    try {
      const [emp, nat, con, proj] = await Promise.all([
        axios.get('/api/admin/employees'),
        axios.get('/api/sheet/natures'),
        axios.get('/api/sheet/contractors'),
        axios.get('/api/sheet/projects'),
      ]);
      setEmployees(emp.data);
      setNatures(nat.data);
      setContractors(con.data);
      setProjects(proj.data);
    } catch (e) {
      /* ignore */
    }
  };

  const fetchExpenses = async (filters) => {
    try {
      const res = await axios.get('/api/employee-expenses/all', { params: filters });
      setExpenses(res.data);
    } catch (e) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      employee: fEmployee || undefined,
      date: fDate || undefined,
      nature: fNature || undefined,
      contractor: fContractor || undefined,
      project: fProject || undefined,
      status: fStatus || undefined,
    }),
    [search, fEmployee, fDate, fNature, fContractor, fProject, fStatus]
  );

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { fetchExpenses(filters); /* eslint-disable-next-line */ }, [filters]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusBadge = (s) => {
    const map = {
      pending: { background: '#fef3c7', color: '#92400e' },
      approved: { background: '#d1fae5', color: '#065f46' },
      rejected: { background: '#fee2e2', color: '#991b1b' },
    };
    const st = map[s] || map.pending;
    return <span className="badge" style={st}>{s}</span>;
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/api/employee-expenses/export', { params: filters, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee-expenses.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const openEdit = (e) => {
    setEditExpense(e);
    setEditForm({
      date: (e.date || '').slice(0, 10),
      expenseNature: e.expenseNature || '',
      contractorName: e.contractorName || '',
      projectName: e.projectName || '',
      billNumber: e.billNumber || '',
      amount: e.amount ?? '',
      paymentMode: e.paymentMode || 'Cash',
      paidBy: e.paidBy || '',
      paidAmount: e.paidAmount ?? '',
      remarks: e.remarks || '',
    });
  };

  const handleEditChange = (ev) => setEditForm((p) => ({ ...p, [ev.target.name]: ev.target.value }));

  const editBalance = (parseFloat(editForm.amount) || 0) - (parseFloat(editForm.paidAmount) || 0);

  const submitEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`/api/employee-expenses/${editExpense._id}`, {
        ...editForm,
        amount: parseFloat(editForm.amount),
        paidAmount: parseFloat(editForm.paidAmount) || 0,
      });
      setExpenses((prev) => prev.map((x) => (x._id === editExpense._id ? res.data : x)));
      toast.success('Expense updated');
      setEditExpense(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (e) => setDeleteExpense(e);

  const confirmDelete = async () => {
    if (!deleteExpense) return;
    try {
      await axios.delete(`/api/employee-expenses/${deleteExpense._id}`);
      setExpenses((prev) => prev.filter((x) => x._id !== deleteExpense._id));
      toast.success('Expense deleted');
      setDeleteExpense(null);
      setViewExpense(null);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openReview = (e, status) => {
    setReview(e);
    setReviewStatus(status);
    setReviewRemark('');
  };

  const submitReview = async () => {
    if (!reviewStatus) return toast.error('Select a status');
    setSaving(true);
    try {
      const res = await axios.patch(`/api/employee-expenses/${review._id}/status`, {
        status: reviewStatus,
        adminRemark: reviewRemark,
      });
      setExpenses((prev) => prev.map((x) => (x._id === review._id ? res.data : x)));
      toast.success('Status updated');
      setReview(null);
      setViewExpense(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const openPay = (e) => {
    setPayExpense(e);
    setPayForm({ amount: '', date: new Date().toISOString().slice(0, 10), remarks: '' });
  };

  const submitPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      const res = await axios.post(`/api/employee-expenses/${payExpense._id}/payments`, payForm);
      setExpenses((prev) => prev.map((x) => (x._id === payExpense._id ? res.data : x)));
      toast.success('Payment recorded');
      setPayExpense(null);
      setViewExpense(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Expense Sheet</h1>
          <p className="page-subtitle">All employee expense submissions</p>
        </div>
        <button className="btn btn-primary" onClick={handleExport}>⬇ Export Excel</button>
      </div>

      <div className="filters" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <input
          className="form-input"
          style={{ flex: '1 1 200px', minWidth: '160px' }}
          placeholder="Search ID, employee, nature, bill..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select" value={fEmployee} onChange={(e) => setFEmployee(e.target.value)}>
          <option value="">All Employees</option>
          {employees.map((em) => (
            <option key={em._id} value={em._id}>{em.employeeId ? `${em.employeeId} · ` : ''}{em.name}</option>
          ))}
        </select>
        <input type="date" className="form-input" value={fDate} onChange={(e) => setFDate(e.target.value)} title="Filter by date" />
        <select className="form-select" value={fNature} onChange={(e) => setFNature(e.target.value)}>
          <option value="">All Natures</option>
          {natures.map((n) => <option key={n._id} value={n.name}>{n.name}</option>)}
        </select>
        <select className="form-select" value={fContractor} onChange={(e) => setFContractor(e.target.value)}>
          <option value="">All Contractors</option>
          {contractors.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
        </select>
        <select className="form-select" value={fProject} onChange={(e) => setFProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p._id} value={p.name}>{p.name}</option>)}
        </select>
        <select className="form-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">All Status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="card-body">
          {expenses.length === 0 ? (
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <h3 className="empty-state-title">No expenses found</h3>
              <p className="empty-state-text">Submissions will appear here automatically</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Expense ID</th>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Nature</th>
                    <th>Contractor</th>
                    <th>Project</th>
                    <th>Bill No.</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Payment Mode</th>
                    <th>Paid By</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Employee Payable</th>
                    <th style={{ textAlign: 'right' }}>Company/Owner Paid</th>
                    <th style={{ textAlign: 'right' }}>Admin Paid</th>
                    <th style={{ textAlign: 'right' }}>Payable Balance</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e._id}>
                      <td style={{ fontWeight: 600 }}>{e.expenseId}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{e.employeeName}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{e.employeeId}</div>
                      </td>
                      <td>{formatDate(e.date)}</td>
                      <td>{e.expenseNature}</td>
                      <td>{e.contractorName}</td>
                      <td>{e.projectName || '—'}</td>
                      <td>{e.billNumber || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                      <td>{e.paymentMode}</td>
                      <td>{e.paidBy || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(e.paidAmount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(e.employeePayable)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(e.companyOwnerPaid)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(e.adminPaidAmount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {Number(e.payableBalance) === 0 && Number(e.employeePayable) > 0
                          ? <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Settled</span>
                          : formatCurrency(e.payableBalance)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: Number(e.balanceAmount) > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatCurrency(e.balanceAmount)}</td>
                      <td>{statusBadge(e.status)}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setViewExpense(e)}>View</button>{' '}
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(e)}>Edit</button>{' '}
                        <button className="btn btn-danger btn-sm" onClick={() => openDelete(e)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View modal */}
      {viewExpense && (
        <div className="modal-overlay" onClick={() => setViewExpense(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Expense {viewExpense.expenseId}</h3>
              <button className="modal-close" onClick={() => setViewExpense(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
                <div><strong>Employee:</strong> {viewExpense.employeeName} ({viewExpense.employeeId})</div>
                <div><strong>Date:</strong> {formatDate(viewExpense.date)}</div>
                <div><strong>Nature:</strong> {viewExpense.expenseNature}</div>
                <div><strong>Contractor:</strong> {viewExpense.contractorName}</div>
                <div><strong>Project:</strong> {viewExpense.projectName || '—'}</div>
                <div><strong>Bill No:</strong> {viewExpense.billNumber || '—'}</div>
                <div><strong>Payment Mode:</strong> {viewExpense.paymentMode}</div>
                <div><strong>Paid By:</strong> {viewExpense.paidBy || '—'}</div>
                <div><strong>Amount:</strong> {formatCurrency(viewExpense.amount)}</div>
                <div><strong>Paid:</strong> {formatCurrency(viewExpense.paidAmount)}</div>
                <div><strong>Employee Payable:</strong> {formatCurrency(viewExpense.employeePayable)}</div>
                <div><strong>Company/Owner Paid:</strong> {formatCurrency(viewExpense.companyOwnerPaid)}</div>
                <div><strong>Admin Paid:</strong> {formatCurrency(viewExpense.adminPaidAmount)}</div>
                <div><strong>Payable Balance:</strong> {formatCurrency(viewExpense.payableBalance)}</div>
                <div><strong>Balance:</strong> {formatCurrency(viewExpense.balanceAmount)}</div>
                <div><strong>Status:</strong> {statusBadge(viewExpense.status)}</div>
              </div>
              {viewExpense.remarks && (
                <div className="form-group" style={{ marginTop: '14px' }}>
                  <label className="form-label">Remarks</label>
                  <div style={{ fontSize: '0.9rem' }}>{viewExpense.remarks}</div>
                </div>
              )}
              {viewExpense.adminRemark && (
                <div className="form-group" style={{ marginTop: '14px' }}>
                  <label className="form-label">Admin Remark</label>
                  <div className="alert" style={{ fontSize: '0.9rem' }}>{viewExpense.adminRemark}</div>
                </div>
              )}
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label className="form-label">Attachments</label>
                <Attachments attachments={viewExpense.attachments} />
              </div>

              <div style={{ marginTop: '20px', borderTop: '1px solid var(--gray-200)', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Payments</label>
                  {Number(viewExpense.payableBalance) > 0 ? (
                    <button className="btn btn-primary btn-sm" onClick={() => openPay(viewExpense)}>+ Record Payment</button>
                  ) : Number(viewExpense.employeePayable) > 0 ? (
                    <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Settled</span>
                  ) : null}
                </div>
                {(viewExpense.paymentHistory || []).length === 0 ? (
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>No payments recorded</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(viewExpense.paymentHistory || []).map((p, i) => (
                      <div key={p._id || i} style={{ display: 'flex', gap: '12px', fontSize: '0.875rem', padding: '6px 8px', background: 'var(--gray-100)', borderRadius: '6px' }}>
                        <span style={{ minWidth: '90px' }}>{formatDate(p.date)}</span>
                        <span style={{ fontWeight: 600 }}>{formatCurrency(p.amount)}</span>
                        <span style={{ color: 'var(--gray-500)', flex: 1 }}>{p.remarks || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '20px', borderTop: '1px solid var(--gray-200)', paddingTop: '16px' }}>
                <label className="form-label">Approve / Reject</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className={`btn ${viewExpense.status === 'approved' ? 'btn-primary' : 'btn-outline'} btn-sm`} onClick={() => openReview(viewExpense, 'approved')}>Approve</button>
                  <button className={`btn ${viewExpense.status === 'rejected' ? 'btn-danger' : 'btn-outline'} btn-sm`} onClick={() => openReview(viewExpense, 'rejected')}>Reject</button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={() => openDelete(viewExpense)}>Delete</button>
              <button className="btn btn-outline" onClick={() => setViewExpense(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Review (approve/reject) modal */}
      {review && (
        <div className="modal-overlay" onClick={() => setReview(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{reviewStatus === 'approved' ? 'Approve' : 'Reject'} {review.expenseId}</h3>
              <button className="modal-close" onClick={() => setReview(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Remark</label>
                <textarea className="form-textarea" placeholder="Add a remark..." value={reviewRemark} onChange={(e) => setReviewRemark(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setReview(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitReview} disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editExpense && (
        <div className="modal-overlay" onClick={() => setEditExpense(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Edit {editExpense.expenseId}</h3>
              <button className="modal-close" onClick={() => setEditExpense(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Date</label>
                <input type="date" name="date" className="form-input" value={editForm.date} onChange={handleEditChange} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Expense Nature</label>
                  <select name="expenseNature" className="form-select" value={editForm.expenseNature} onChange={handleEditChange}>
                    {natures.map((n) => <option key={n._id} value={n.name}>{n.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contractor</label>
                  <select name="contractorName" className="form-select" value={editForm.contractorName} onChange={handleEditChange}>
                    {contractors.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <select name="projectName" className="form-select" value={editForm.projectName} onChange={handleEditChange}>
                    <option value="">Select project</option>
                    {projects.map((p) => <option key={p._id} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bill Number</label>
                <input type="text" name="billNumber" className="form-input" value={editForm.billNumber} onChange={handleEditChange} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <input type="number" name="amount" className="form-input" step="0.01" min="0" value={editForm.amount} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Mode</label>
                  <select name="paymentMode" className="form-select" value={editForm.paymentMode} onChange={handleEditChange}>
                    {PAYMENT_MODE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Paid By</label>
                  <input type="text" name="paidBy" className="form-input" value={editForm.paidBy} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Paid</label>
                  <input type="number" name="paidAmount" className="form-input" step="0.01" min="0" value={editForm.paidAmount} onChange={handleEditChange} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Balance (auto)</label>
                <input type="text" className="form-input" value={formatCurrency(editBalance)} disabled style={{ background: 'var(--gray-100)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Employee Payable (auto)</label>
                  <input type="text" className="form-input" value={formatCurrency((editForm.paidBy || '').trim().toLowerCase() === 'self' ? (parseFloat(editForm.paidAmount) || 0) : 0)} disabled style={{ background: 'var(--gray-100)' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Company/Owner Paid (auto)</label>
                  <input type="text" className="form-input" value={formatCurrency((editForm.paidBy || '').trim().toLowerCase() === 'self' ? 0 : (parseFloat(editForm.paidAmount) || 0))} disabled style={{ background: 'var(--gray-100)' }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea name="remarks" className="form-textarea" value={editForm.remarks} onChange={handleEditChange} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditExpense(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payExpense && (
        <div className="modal-overlay" onClick={() => setPayExpense(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Record Payment</h3>
              <button className="modal-close" onClick={() => setPayExpense(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Employee</label>
                <input type="text" className="form-input" value={payExpense.employeeName} disabled style={{ background: 'var(--gray-100)' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee Payable</label>
                <input type="text" className="form-input" value={formatCurrency(payExpense.employeePayable)} disabled style={{ background: 'var(--gray-100)' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Remaining Payable</label>
                <input type="text" className="form-input" value={formatCurrency(payExpense.payableBalance)} disabled style={{ background: 'var(--gray-100)' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount to Pay *</label>
                <input type="number" className="form-input" step="0.01" min="0" max={payExpense.payableBalance} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date</label>
                <input type="date" className="form-input" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea className="form-textarea" placeholder="Optional remarks" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPayExpense(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPayment} disabled={saving}>{saving ? 'Saving...' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteExpense && (
        <div className="modal-overlay" onClick={() => setDeleteExpense(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Expense</h3>
              <button className="modal-close" onClick={() => setDeleteExpense(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                Are you sure you want to delete this expense? This action will permanently remove the expense from the employee account and all related records.
              </p>
              {deleteExpense.attachments && deleteExpense.attachments.length > 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--danger)' }}>
                  This will also delete {deleteExpense.attachments.length} uploaded proof file(s).
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteExpense(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete}>Confirm Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpenseSheet;
